import { ReactNode, useCallback } from 'react';

import { formatName } from '@libs/formatNames';
import { InlineInput } from '@ui/components/InlineInput';
import { TableCell, TableRow } from '@ui/components/Table';

import styles from '@features/club/ui/ClubParticipantsForm/components/List/List.module.css';
import { MemberDto, ParticipantDto, RcfAttributes } from '@features/shared/api/typings';
import { useRcfAttributesControlProps } from '@features/shared/hooks/useRcfAttributesControlProps';
import { RcfAttributesControl } from '@features/user/ui/RcfAttributesControl/RcfAttributesControl';

type ParticipantListItemProps = {
  participant: MemberDto | ParticipantDto;
  renderActions?: (participant: ParticipantDto | MemberDto) => ReactNode;
  highlighted?: boolean;
  onMemberGroupNameChange?: (userId: string, newGroupName: string | null) => void;
  memberGroupNameOptions?: string[];
  isRcf: boolean;
  isRcfModalOpen: boolean;
  setRcfModalOpen: (userId: string | null) => void;
  onRcfAttributesChange?: (rcfAttributes: RcfAttributes) => void;
};

export function ParticipantListItem({
  participant,
  renderActions,
  highlighted,
  onMemberGroupNameChange,
  memberGroupNameOptions,
  isRcf,
  isRcfModalOpen,
  setRcfModalOpen,
  onRcfAttributesChange,
}: ParticipantListItemProps) {
  const rcfProps = useRcfAttributesControlProps(participant, setRcfModalOpen);

  const groupName = (participant as MemberDto).groupName || '';

  const onGroupNameChange = useCallback(
    (newGroupName: string | null) => {
      onMemberGroupNameChange?.(participant.userId, newGroupName);
    },
    [onMemberGroupNameChange, participant.userId],
  );

  return (
    <TableRow hoverable active={highlighted}>
      <TableCell className={styles.nameColumn}>{formatName(participant)}</TableCell>
      <TableCell className={styles.mailColumn} leftGap>
        <span title={participant.email ?? undefined}>{participant.email}</span>
      </TableCell>
      <TableCell className={styles.groupColumn} leftGap>
        <InlineInput
          value={groupName}
          onChange={onGroupNameChange}
          options={memberGroupNameOptions}
        />
      </TableCell>
      {isRcf && (
        <TableCell>
          {onRcfAttributesChange && (
            <RcfAttributesControl
              onChange={onRcfAttributesChange}
              isModalOpen={isRcfModalOpen}
              {...rcfProps}
            />
          )}
        </TableCell>
      )}
      <TableCell leftGap right>
        {renderActions && renderActions(participant)}
      </TableCell>
    </TableRow>
  );
}
